/**
 * 스토리지 설정 관련 타입 정의
 * NAS 하이브리드 스토리지 시스템용
 */

import { Timestamp } from 'firebase/firestore';

/**
 * 스토리지 모드
 * - firebase-only: Firebase Storage만 사용
 * - hybrid: Firebase + NAS 하이브리드 사용
 */
export type StorageMode = 'firebase-only' | 'hybrid';

/**
 * NAS 제조사
 */
export type NasProvider = 'synology' | 'qnap' | 'other';

/**
 * 연결 타입
 */
export type ConnectionType = 'webdav' | 'smb' | 'ftp';

/**
 * 연결 상태
 */
export type ConnectionStatus = 'connected' | 'disconnected' | 'error' | 'testing';

/**
 * NAS 연결 설정
 */
export interface NasConfig {
  enabled: boolean;
  provider: NasProvider;
  connectionType: ConnectionType;
  
  // 연결 정보
  host: string;           // yournas.synology.me
  port: number;           // 5006 (WebDAV 기본값)
  username: string;       // egmms_app
  password: string;       // 암호화 필요
  sharedFolder: string;   // egmms_files
  
  // 상태
  status: ConnectionStatus;
  lastTested?: Timestamp;
  lastError?: string;
}

/**
 * 저장 규칙
 */
export interface StorageRules {
  // 크기 임계값 (MB)
  sizeThreshold: number;              // 이상은 NAS에 저장
  
  // 자동 생성 옵션
  nasAutoFolderStructure: boolean;   // 폴더 구조 자동 생성
  nasCreateInfoFiles: boolean;        // _info.txt 자동 생성
  
  // Firebase에 저장할 파일 타입
  firebaseFiles: string[];            // ['thumbnail', 'qr', 'small-document']
  
  // NAS에 저장할 파일 타입
  nasFiles: string[];                 // ['3d-model', 'cad-drawing', 'large-file']
}

/**
 * 전체 스토리지 설정
 */
export interface StorageConfig {
  // 기본 설정
  mode: StorageMode;
  updatedAt: Timestamp | string;  // Timestamp 또는 ISO string
  updatedBy?: string;             // 관리자 UID (optional)
  
  // NAS 설정 (hybrid 모드일 때만)
  nas?: NasConfig;
  nasConnection?: {               // 임시 호환성 (Phase 1)
    proxyUrl?: string;
  };
  
  // 저장 규칙 (optional, 기본값 사용)
  rules?: StorageRules;
  storageRules?: {                // 임시 호환성 (Phase 1)
    largeSizeThreshold?: number;
    fileTypeRules?: {
      images?: 'firebase' | 'nas';
      documents?: 'firebase' | 'nas';
      models?: 'firebase' | 'nas';
    };
  };
}

/**
 * 기본 저장 규칙
 */
export const DEFAULT_STORAGE_RULES: StorageRules = {
  sizeThreshold: 10,  // 10MB
  nasAutoFolderStructure: true,
  nasCreateInfoFiles: true,
  firebaseFiles: ['thumbnail', 'qr', 'small-document'],
  nasFiles: ['3d-model', 'cad-drawing', 'large-file', 'video']
};

/**
 * 기본 NAS 설정
 */
export const DEFAULT_NAS_CONFIG: Omit<NasConfig, 'password'> = {
  enabled: false,
  provider: 'synology',
  connectionType: 'webdav',
  host: '',
  port: 5006,
  username: '',
  sharedFolder: 'egmms_files',
  status: 'disconnected'
};
